<script lang="ts" setup>
import type { IconName } from '~ds/lib/icons';
import type { SocialLoginProvider } from '~kui/types/auth';

import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { IntegrationLoginProvider } from '~kui/types/auth';
import { isIntegrationLoginProvider } from '~kui/utils/auth';
import { useI18n } from 'vue-i18n';

/**
 * The social sign-in buttons, plus one button per integration provider named in
 * `integrationProviders` (the cluster's `integrationsConfig.loginButtons`). A key this component
 * knows renders its button; an unknown key renders nothing. The setting shows or hides a button
 * and enables nothing else.
 */
interface Props {
  disabled?: boolean;
  integrationProviders?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  integrationProviders: () => [],
});

const emit = defineEmits<{
  socialLogin: [provider: SocialLoginProvider | IntegrationLoginProvider];
}>();

const { t } = useI18n();

const INTEGRATION_BUTTONS: Record<IntegrationLoginProvider, { icon: IconName; labelKey: string }> = {
  [IntegrationLoginProvider.Clever]: { icon: ICONS.brandClever, labelKey: 'kui.auth.continueWithClever' },
  [IntegrationLoginProvider.FakeClever]: { icon: ICONS.brandClever, labelKey: 'kui.auth.continueWithFakeClever' },
};

const integrationButtons = computed(() =>
  props.integrationProviders
    .filter(isIntegrationLoginProvider)
    .map(key => ({ key, ...INTEGRATION_BUTTONS[key] })),
);
</script>

<template>
  <div class="w-full">
    <div class="my-6 flex w-full items-center gap-4">
      <div class="h-px flex-1 bg-border" />
      <Text
        as="span"
        size="sm"
      >
        {{ t('general.auth.orContinueWith') }}
      </Text>
      <div class="h-px flex-1 bg-border" />
    </div>

    <div
      class="w-full space-y-3"
      data-testid="social-login"
    >
      <Button
        data-testid="social-login-google"
        variant="outline"
        size="sm"
        :disabled="disabled"
        class="w-full rounded-lg"
        type="button"
        @click="emit('socialLogin', 'google')"
      >
        <Icon :name="ICONS.brandGoogle" />
        {{ t('general.auth.continueWithGoogle') }}
      </Button>

      <Button
        data-testid="social-login-microsoft"
        variant="outline"
        size="sm"
        :disabled="disabled"
        class="w-full rounded-lg"
        type="button"
        @click="emit('socialLogin', 'microsoft')"
      >
        <Icon :name="ICONS.brandMicrosoft" />
        {{ t('general.auth.continueWithMicrosoft') }}
      </Button>

      <Button
        v-for="button in integrationButtons"
        :key="button.key"
        :data-testid="`social-login-${button.key}`"
        variant="outline"
        size="sm"
        :disabled="disabled"
        class="w-full rounded-lg"
        type="button"
        @click="emit('socialLogin', button.key)"
      >
        <Icon :name="button.icon" />
        {{ t(button.labelKey) }}
      </Button>
    </div>
  </div>
</template>
